import { useLayoutEffect, type RefObject } from 'react';
import { CARD_GRID_LAYOUT, FLUSH_FULL_CARD_ROWS } from 'DesignTokens';

/** Flush the last card in each full flex row without spreading underfull rows. */
export function useCardRowAlignment(
  cardRowRef: RefObject<HTMLDivElement | null>,
  cardCount: number,
) {
  useLayoutEffect(() => {
    if (CARD_GRID_LAYOUT || !FLUSH_FULL_CARD_ROWS) return;
    const container = cardRowRef.current;
    if (!container) return;

    const alignFullRows = () => {
      const cards = Array.from(container.children) as HTMLElement[];
      cards.forEach((card) => {
        card.style.marginLeft = '';
      });
      if (cards.length < 2) return;

      const containerWidth = container.clientWidth;
      const cardWidth = cards[0].getBoundingClientRect().width;
      if (!containerWidth || !cardWidth) return;

      const gap = 30; // matches the columnGap in PropertyCardGroup
      const capacity = Math.max(1, Math.floor((containerWidth + gap) / (cardWidth + gap)));
      if (capacity <= 1) return;

      let rowStart = 0;
      for (let i = 1; i <= cards.length; i++) {
        const rowEnded = i === cards.length || cards[i].offsetTop !== cards[rowStart].offsetTop;
        if (rowEnded) {
          if (i - rowStart === capacity) {
            cards[i - 1].style.marginLeft = 'auto';
          }
          rowStart = i;
        }
      }
    };

    alignFullRows();
    // A card whose React key changes (e.g. when its content loads) is replaced by a new element
    // without the margin set above, and that alone doesn't resize the container.
    const childObserver =
      typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(alignFullRows);
    childObserver?.observe(container, { childList: true });
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(alignFullRows);
    resizeObserver?.observe(container);
    return () => {
      childObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [cardRowRef, cardCount]);
}
